import React, { memo, useContext, useEffect, useState, useRef } from 'react'
import { AiFillWarning } from '@react-icons/all-files/ai/AiFillWarning'
import { useSelector, useDispatch } from 'react-redux'

// Internal imports
import Button from 'components/common/button'
import Delivery from 'components/common/delivery'
import Pickup from 'components/common/pickup'
import { Spinner } from 'components/common/spinner'
import { LanguageContext } from 'context/LanguageContext'
import { 
    trackCateringLocationSelectionFteSelectorOpened,
    trackCateringLocationSelectionFteSelectorExited
} from 'middleware/helper'
import { clearBasket } from 'modules/basket/actions'
import { getConfiguration } from 'modules/configuration/actions'
import {
    transferAllTemp,
    getStoresByLocation,
    getStoreDetails,
    setTempAddress,
    setTempStoreDetails,
    setChoiceButton,
    setClearCartMessage,
} from 'modules/location/actions'
import { getCurrentOrdersList } from 'modules/orders/actions'
import { getUserProfile } from 'modules/user/actions'
import { orderTypes, isDeliveryMode } from 'utils/appUtils'

const AddressOptions = memo(({ show, hide, registration, setShowRegister, signInFromBasket, checkout, setShowDateTimePicker }) => {
    const { strings } = useContext(LanguageContext)

    const dispatch = useDispatch()
    const isDelivery = isDeliveryMode()
    const isRegistration = registration || false
    const selectedAddress = useSelector(state => state.location.getIn(['selectedAddress']))
    const selectedStore = useSelector(state => state.location.getIn(['selectedStore']))
    const cartPatchInProgress = useSelector(state => state.basket.getIn(['cartPatchInProgress']))
    const basketItems = useSelector(state => state.basket.get('basketItems'))
    const hasPlatterItems = useSelector(state => state.basket.get('hasPlatterItems'))
    const appSettingsIsCateringMode = useSelector(state => state.appSettings.get('isCateringMode'))
    const isCateringMode = hasPlatterItems || appSettingsIsCateringMode
    const cateringData = useSelector(state => state.catering)
    const itemsAddedToCart = basketItems ? basketItems.toJS() : []
    const [shouldTransfer, setShouldTransfer] = useState(true)
    const unMount = React.useRef()
    unMount.current = () => {
        if (shouldTransfer) {
            dispatch(transferAllTemp())
        } else {
            dispatch(setTempStoreDetails(null))
            dispatch(setTempAddress(null))
            isDelivery && dispatch(getStoresByLocation(selectedAddress?.toJS()))
            !isDelivery && dispatch(getStoreDetails(selectedStore?.toJS(), false))
            dispatch(getUserProfile())
            //we can pass country below too for config
            dispatch(getConfiguration())
            dispatch(getCurrentOrdersList())
        }
    }

    const [isClearBasket, setIsClearBasket] = useState(false)
    const modalOpenTimeRef = useRef(null)
    const hasTrackedModalOpen = useRef(false)
    
    // In catering mode, always use pickup mode regardless of what was previously selected
    const [currentServiceMode, setCurrentServiceMode] = useState(
        isCateringMode ? orderTypes.PICKUP : (isDelivery ? orderTypes.DELIVERY : orderTypes.PICKUP)
    )
    
    // Track location selector opened for catering
    useEffect(() => {
        if (show && isCateringMode && !hasTrackedModalOpen.current) {
            modalOpenTimeRef.current = Date.now()
            const previousOrderType = isDelivery ? 'delivery' : 'pickup'
            const currentLocation = selectedAddress?.toJS()?.formatted_address || cateringData?.address?.formatted_address || ''
            
            trackCateringLocationSelectionFteSelectorOpened(previousOrderType, currentLocation)
            hasTrackedModalOpen.current = true
        }
        
        // Track selector exited when modal closes
        if (!show && hasTrackedModalOpen.current && modalOpenTimeRef.current && isCateringMode) {
            const timeInSelector = Math.floor((Date.now() - modalOpenTimeRef.current) / 1000) // seconds
            const itemsInCart = itemsAddedToCart.length
            const previousOrderType = isDelivery ? 'delivery' : 'pickup'
            
            trackCateringLocationSelectionFteSelectorExited(itemsInCart, timeInSelector, previousOrderType)
            hasTrackedModalOpen.current = false
            modalOpenTimeRef.current = null
        }
    }, [show, isCateringMode])
    
    // Always enforce pickup mode in catering
    useEffect(() => {
        if (isCateringMode && currentServiceMode !== orderTypes.PICKUP) {
            setCurrentServiceMode(orderTypes.PICKUP)
        }
        dispatch(setChoiceButton(currentServiceMode))
    }, [currentServiceMode, isCateringMode, dispatch])

    const getAddressForMode = () => {
        if (isCateringMode) {
            return cateringData?.address || {}
        }
        return selectedAddress ? selectedAddress?.toJS() : {}
    }

    const [deliveryAddress, setDeliveryAddress] = useState(
        currentServiceMode === orderTypes.DELIVERY
            ? getAddressForMode()
            : {}
    )
    const [pickupAddress, setPickupAddress] = useState(
        currentServiceMode === orderTypes.PICKUP
            ? getAddressForMode()
            : {}
    )

    const [tempMode, setTempMode] = useState(null)
    const [showFeedback, setShowFeedback] = useState(false)

    useEffect(() => {
        show && (document.body.style.overflow = 'hidden')
        return () => {
            document.body.style.overflow = ''
        }
    }, [show])

    useEffect(() => {
        return () => {
            unMount.current()
            dispatch(setClearCartMessage(false))            
        }
    }, [])

    useEffect(() => {
        if (isClearBasket && !cartPatchInProgress) {
            setShowFeedback(false)
            setIsClearBasket(false)
        }
    }, [cartPatchInProgress])

    const hideFeedback = () => {
        setShowFeedback(false)
        setTempMode(null)
    }

    const clearCompleteBasket = () => {
        setIsClearBasket(true)
        dispatch(clearBasket())
        setCurrentServiceMode(tempMode)
        setShowFeedback(false)
    }

    const handleChangeMode = event => {
        // Don't allow changing to delivery mode in catering
        if (isCateringMode && event.target.value === orderTypes.DELIVERY) {
            return;
        }
        
        const isDeliveryByType = isDeliveryMode()
        if (
            itemsAddedToCart.length &&
            ((currentServiceMode === orderTypes.DELIVERY && isDeliveryByType) ||
                (currentServiceMode === orderTypes.PICKUP && !isDeliveryByType))
        ) {
            setShowFeedback(true)
            setTempMode(event.target.value)
        } else {
            setShowFeedback(false)
            setCurrentServiceMode(event.target.value)
            setTempMode(null)
        }
    }
    useEffect(() => {
        dispatch(setClearCartMessage(showFeedback))
    }, [showFeedback])

    const renderModes = () => {
        // Don't render the mode buttons in catering mode
        if (isCateringMode) {
            return null;
        }
        
        return (
            <div className={'choiceBtns'}>
                <button
                    className={`${currentServiceMode === orderTypes.DELIVERY && 'selected'}`}
                    id="delivery"
                    onClick={handleChangeMode}
                    value="delivery"
                >
                    {strings.ADDRESS_DELIVERY}
                </button>
                <button
                    className={`${currentServiceMode === orderTypes.PICKUP && 'selected'}`}
                    id="pickup"
                    onClick={handleChangeMode}
                    value="pickup"
                >
                    {strings.ADDRESS_PICKUP}
                </button>
            </div>
        )
    }

    if (showFeedback) {
        if (cartPatchInProgress) {
            return <Spinner width={'5rem'} height={'5rem'} />
        }

        return (
            <div
                className={'feedBackForm'}
                tabIndex="0"
                aria-label={`${strings.basketItemsRemovalWarning} ${strings.basketItemsRemovalDetailedWarning} `}
            >
                <div className={'feedBackForm__content'}>
                    <AiFillWarning className={'feedBackForm__content-warningIcon'} />
                    <p className={'feedBackForm__content-warningMsg1'}>
                        {strings.basketItemsRemovalWarning}
                    </p>
                    <p className={'feedBackForm__content-warningMsg2'}>
                        {isDelivery
                            ? strings.basketItemsRemovalDetailedWarning
                            : strings.basketItemsRemovalDetailedWarningDelivery}
                    </p>
                </div>
                <div className={'feedBackForm__action'}>
                    <Button
                        className={'cancel cancel-lg'}
                        onClick={hideFeedback}
                        aria-labelledby="cancel button"
                    >
                        {strings.cancelButton}
                    </Button>
                    <Button
                        className={'confirm confirm-lg'}
                        onClick={clearCompleteBasket}
                        aria-labelledby="clear basket button"
                    >
                        {strings.clearBasket}
                    </Button>
                </div>
            </div>
        )
    }

    return currentServiceMode === 'delivery' ? (
        <Delivery
            deliveryAddress={deliveryAddress}
            pickupAddress={pickupAddress}
            setDeliveryAddress={setDeliveryAddress}
            hide={hide}
            isRegistration={isRegistration}
            setPickupAddress={setPickupAddress}
            setShowRegister={setShowRegister}
            setShouldTransferTemp={setShouldTransfer}
            signInFromBasket={signInFromBasket}
        >
            {renderModes()}
        </Delivery>
    ) : (
        <Pickup
            pickupAddress={pickupAddress}
            setPickupAddress={setPickupAddress}
            hide={hide}
            isRegistration={isRegistration}
            setShowRegister={setShowRegister}
            setShouldTransferTemp={setShouldTransfer}
            signInFromBasket={signInFromBasket}
            checkout={checkout}
            setShowDateTimePicker={setShowDateTimePicker}
        >
            {renderModes()}
        </Pickup>
    )
})

export default AddressOptions
